import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      // Check for guest user first (instant, no API call)
      const guestUser = localStorage.getItem('guestUser');
      if (guestUser) {
        try {
          const parsed = JSON.parse(guestUser);
          // Validate guest has session_token (required for API auth)
          if (!parsed.session_token) {
            console.log('Guest session missing token - clearing');
            localStorage.removeItem('guestUser');
          } else {
            setUser(parsed);
            setIsGuest(true);
            setIsLoading(false);
            return;
          }
        } catch (e) {
          localStorage.removeItem('guestUser');
        }
      }

      // Check localStorage first for cached user (instant)
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          const parsed = JSON.parse(savedUser);
          // Validate user has session_token (required for API auth)
          if (!parsed.session_token) {
            console.log('User session missing token - need to re-login');
            localStorage.removeItem('user');
            setIsLoading(false);
            return; // Will redirect to login
          }
          
          // Show cached user immediately while validating
          setUser(parsed);
          setIsLoading(false);
          
          // Validate in background (non-blocking)
          api.get('/api/auth/me')
            .then(response => {
              // Keep the session_token from localStorage since /me doesn't return it
              const updatedUser = { ...response.data, session_token: parsed.session_token };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            })
            .catch(() => {
              // Session expired, clear
              localStorage.removeItem('user');
              setUser(null);
            });
          return;
        } catch (e) {
          localStorage.removeItem('user');
        }
      }

      // No cached user, try to get from backend
      try {
        const response = await api.get('/api/auth/me', { timeout: 5000 });
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (error) {
        // No session, that's fine for landing page
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Handle Google OAuth credential - called from GoogleLogin component
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = useCallback(async (credential) => {
    try {
      const response = await api.post('/api/auth/google', { credential });
      
      const userData = {
        ...response.data,
        session_token: response.data.session_token
      };
      
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsGuest(false);
      
      // Navigate to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }, []);

  const loginAsGuest = async () => {
    try {
      // Create guest session on backend
      const response = await api.post('/api/auth/guest', {});
      
      const guestUser = {
        user_id: response.data.user_id,
        email: response.data.email,
        name: response.data.name,
        picture: null,
        isGuest: true,
        session_token: response.data.session_token
      };
      
      localStorage.setItem('guestUser', JSON.stringify(guestUser));
      setUser(guestUser);
      setIsGuest(true);
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Guest login error:', error);
      // Fallback to local-only guest mode
      const guestUser = {
        user_id: 'guest_' + Date.now(),
        email: 'guest@jafai.com',
        name: 'Guest User',
        picture: null,
        isGuest: true
      };
      localStorage.setItem('guestUser', JSON.stringify(guestUser));
      setUser(guestUser);
      setIsGuest(true);
      window.location.href = '/dashboard';
    }
  };

  const logout = async () => {
    try {
      if (!isGuest) {
        await api.post('/api/auth/logout', {});
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('guestUser');
      setUser(null);
      setIsGuest(false);
      window.location.href = '/';
    }
  };

  const setUserData = (userData) => {
    // Ensure session_token is preserved in stored data
    const userToStore = {
      ...userData,
      session_token: userData.session_token
    };
    setUser(userToStore);
    localStorage.setItem('user', JSON.stringify(userToStore));
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, isGuest, handleGoogleLogin, loginAsGuest, logout, setUserData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
